import {
    Division,
    Grade,
    IdentityPerson,
    IdentityTeam,
    Location,
    Person,
    SkillLevel,
    Team,
    UserAccount,
    WeightClass,
    WrestlerWaiver,
} from '@reg/shared/model';
import { v4 as UUID } from 'uuid';
import isNil from 'lodash-es/isNil';
import { WrestlerSeedingCriteria } from '@reg/shared/model/wrestler-seeding-criteria.model';
import { format } from 'date-fns';
import { DateFormat } from '../enum';
import { convertToDateTime } from '../helper/dateTime-helper';

/**
 * Model to hold a Wrestler data for wrestling. It's used for grouping/categorizing
 * wrestlers together for bracketing and event management purposes
 */
export class Wrestler extends Person {
    public static ENTITY_NAME: string = 'Wrestler';
    public static REG_STATUS_IN_PROGRESS: string = 'in-progress';
    public static REG_STATUS_COMPLETE: string = 'complete';
    public static SOURCE_ONLINE_REGISTRATION: string = 'online-registration';
    public static SOURCE_ONLINE_EVENT_ADMIN: string = 'online-event-admin';
    public static SOURCE_ONLINE_RESULTS: string = 'online-results';

    public isNew: boolean = true;
    public eventId: string = null;
    public division: Division = null;
    public team: Team = null;
    public grade: Grade = null;
    public exactWeight: number = null;
    public exactWeight2: number = null;
    public exactWeight3: number = null;
    public weightClass: WeightClass = null;
    public skillLevel: SkillLevel = null;
    public withdrawn: boolean = false;
    public paid: boolean = false;
    public onlineRegistrationStatus: string = null;
    public usaWrestlingCardNum: number = null;
    public waivers: WrestlerWaiver[] = [];
    public seedingCriteria: WrestlerSeedingCriteria[] = [];
    public identityPersonId: string = null;
    public location: Location;
    public source: string = null;
    public createdByUserAccount: UserAccount;
    public record: string;
    public seed: number;
    public displayName: string = null;

    private _firstName: string = null;
    set firstName(firstName: string) {
        this._firstName = firstName && firstName.trim();
    }

    get firstName(): string {
        return this._firstName;
    }

    private _lastName: string = null;
    set lastName(lastName: string) {
        this._lastName = lastName && lastName.trim();
    }

    get lastName(): string {
        return this._lastName;
    }

    private _nickname: string = null;
    set nickname(nickname: string) {
        this._nickname = nickname && nickname.trim();
    }

    get nickname(): string {
        return this._nickname;
    }

    get recordPercentage(): number {
        if (!this.record) {
            return;
        }

        const wins: number = this.wins;
        let losses: number = 0;
        let ties: number = 0;

        const recordSplit: string[] = this.record.split('-');

        if (recordSplit.length > 1) {
            losses = parseFloat(recordSplit[1]);
        }
        if (recordSplit.length > 2) {
            ties = parseFloat(recordSplit[2]);
        }

        const total: number = wins + losses + ties;

        if (total) {
            return (wins + (0.5 * ties)) / total;
        }

        return 0;
    }

    public get wins(): number {
        if (!this.record) {
            return;
        }

        const recordSplit: string[] = this.record.split('-');

        if (recordSplit.length > 0) {
            return parseFloat(recordSplit[0]);
        }

        return 0;
    }

    public static getFields(): string[] {
        return [
            'firstName',
            'lastName',
            'nickname',
            'eventId',
            'division',
            'divisionId',
            'team',
            'dateOfBirth',
            'grade',
            'exactWeight',
            'exactWeight2',
            'exactWeight3',
            'weightClass',
            'weightClassId',
            'withdrawn',
            'paid',
            'onlineRegistrationStatus',
            'usaWrestlingCardNum',
            'waivers',
            'seedingCriteria',
            'identityPersonId',
            'location',
            'source',
            'skillLevel',
            'skillLevelId',
            'createdByUserAccount',
            'createdByUserId',
            'record',
            'seed',
        ];
    }

    /**
     * Constructor
     */
    constructor(eventId: string = '', onlineRegistrationStatus: string = null, source: string = null) {
        super();
        this.id = UUID();
        this.isNew = true;
        this.eventId = eventId;
        this.team = new Team(eventId);
        this.division = new Division(eventId);
        this.onlineRegistrationStatus = onlineRegistrationStatus;
        this.location = new Location();
        this.source = source;
    }

    public calculateHighSchoolGradYear(): number {
        let highSchoolGradYear: number;

        if (this.grade && this.grade.highSchoolGradYear) {
            highSchoolGradYear = this.grade.highSchoolGradYear;
        }

        if (!highSchoolGradYear &&
            this.dateOfBirth &&
            this.age) {
            // Calculate the best we can the perons's high school graduation year based on their date of birth
            highSchoolGradYear = IdentityPerson.calculateHSGraduationYear(this.dateOfBirth);
        }

        return highSchoolGradYear;
    }

    public fillFromIdentity(identityPerson: IdentityPerson, grades?: Grade[], identityTeam?: IdentityTeam): Wrestler {
        if (identityPerson) {
            this.firstName = identityPerson.firstName;
            this.lastName = identityPerson.lastName;
            this.identityPersonId = identityPerson.id;

            if (identityPerson.location) {
                this.location = identityPerson.location.clone();
            }

            if (identityPerson.dateOfBirth) {
                this.dateOfBirth = identityPerson.dateOfBirth;
            }

            if (identityPerson.usaWrestlingNumber) {
                this.usaWrestlingCardNum = identityPerson.usaWrestlingNumber;
            }

            if (!this.team || !this.team.name) {
                this.team = (new Team(this.eventId)).fillFromIdentityTeam(identityTeam);
            }

            if (grades &&
                (this.isNew || !this.grade) &&
                identityPerson.highSchoolGradYear) {
                const grade: Grade = grades.filter(
                    (x: Grade) => x.highSchoolGradYear === identityPerson.highSchoolGradYear,
                )[0];

                if (grade) {
                    this.grade = grade;
                }
            }
        } else {
            this.identityPersonId = null;
        }

        return this;
    }

    public clone(): Wrestler {
        const clone: Wrestler = new Wrestler();
        clone.id = this.id;
        clone.isNew = this.isNew;
        clone.eventId = this.eventId;
        clone.firstName = this.firstName;
        clone.lastName = this.lastName;
        clone.nickname = this.nickname;
        clone.exactWeight = this.exactWeight;
        clone.exactWeight2 = this.exactWeight2;
        clone.exactWeight3 = this.exactWeight3;
        clone.withdrawn = this.withdrawn;
        clone.paid = this.paid;
        clone.onlineRegistrationStatus = this.onlineRegistrationStatus;
        clone.usaWrestlingCardNum = this.usaWrestlingCardNum;
        clone.identityPersonId = this.identityPersonId;
        clone.source = this.source;
        clone.record = this.record;
        clone.seed = this.seed;
        clone.displayName = this.displayName;

        if (this.team) {
            clone.team = this.team.clone();
        }
        if (this.dateOfBirth) {
            clone.dateOfBirth = new Date(this.dateOfBirth.getTime());
        }
        if (this.grade) {
            clone.grade = this.grade.clone();
        }
        if (this.weightClass) {
            clone.weightClass = this.weightClass.clone();
        }
        if (this.skillLevel) {
            clone.skillLevel = this.skillLevel.clone();
        }
        if (this.division) {
            clone.division = this.division.clone();
        }
        if (this.location) {
            clone.location = this.location.clone();
        }
        if (this.waivers) {
            clone.waivers = this.waivers.map((w: WrestlerWaiver) => w.clone());
        }
        if (this.seedingCriteria) {
            clone.seedingCriteria = this.seedingCriteria.map((w: WrestlerSeedingCriteria) => w.clone());
        }

        return clone;
    }

    public formatNameFirstLast(): string {
        let name: string = this.firstName || '';

        if (this.lastName) {
            name += name ? ' ' : '';
            name += this.lastName;
        }

        return name;
    }

    public formatNameLastFirst(): string {
        let name: string = this.lastName || '';

        if (this.firstName) {
            name += name ? ', ' : '';
            name += this.firstName;
        }

        return name;
    }

    public formatTeamState(): string {
        let teamState: string = '';
        if (this.team) {
            teamState = this.team.name;
        }

        if (this.team.location && this.team.location.state) {
            if (teamState) {
                teamState += ', ';
            }

            teamState += this.team.location.state;
        }

        return teamState;
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'wrestler';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.firstName = this.firstName;
        json.data.attributes.lastName = this.lastName;
        json.data.attributes.nickname = this.nickname;
        json.data.attributes.onlineRegistrationStatus = this.onlineRegistrationStatus;
        json.data.attributes.paid = this.paid;
        json.data.attributes.identityPersonGuid = this.identityPersonId;
        json.data.attributes.usaWrestlingCardNum = null;
        json.data.attributes.record = this.record;
        json.data.attributes.seed = this.seed ? parseInt(this.seed.toString(), null) : null;

        if (this.usaWrestlingCardNum) {
            json.data.attributes.usaWrestlingCardNum = parseInt(this.usaWrestlingCardNum.toString(), null);
        }

        if (this.isNew) {
            json.data.attributes.source = this.source;
        }

        if (this.location) {
            json.data.attributes.location = this.location.serialize();
        }

        json.data.attributes.withdrawn = this.withdrawn;
        json.data.attributes.exactWeight = !isNil(this.exactWeight) ? parseFloat(this.exactWeight.toString()) : null;
        json.data.attributes.exactWeight2 = !isNil(this.exactWeight2) ? parseFloat(this.exactWeight2.toString()) : null;
        json.data.attributes.exactWeight3 = !isNil(this.exactWeight3) ? parseFloat(this.exactWeight3.toString()) : null;
        json.data.attributes.dateOfBirth = null;

        if (this.dateOfBirth) {
            json.data.attributes.dateOfBirth = format(this.dateOfBirth, DateFormat.YYYYMMDD);
        }

        json.data.relationships = {};

        json.data.relationships.grade = { data: { type: 'grade', id: this.grade ? this.grade.id : null } };

        if (this.team) {
            const team: any = {};
            team.data = { type: 'team', id: this.team.id };
            json.data.relationships.team = team;
        }

        if (this.weightClass) {
            const weightClass: any = {};
            weightClass.data = { type: 'weightClass', id: this.weightClass.id };
            json.data.relationships.weightClass = weightClass;
        }

        if (this.skillLevel) {
            const skillLevel: any = {};
            skillLevel.data = { type: 'skillLevel', id: this.skillLevel.id };
            json.data.relationships.skillLevel = skillLevel;
        }

        if (this.division) {
            const division: any = {};
            division.data = { type: 'division', id: this.division.id };
            json.data.relationships.division = division;
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize to json api format
     */
    public deserialize(json: any, included: any[]): Wrestler {
        const data: any = json.data || json;
        const attrs: any = data.attributes || {};
        const relationships: any = data.relationships || {};

        this.isNew = false;
        this.id = data.id;
        this.firstName = attrs.firstName;
        this.lastName = attrs.lastName;
        this.nickname = attrs.nickname;
        this.exactWeight = attrs.exactWeight;
        this.exactWeight2 = attrs.exactWeight2;
        this.exactWeight3 = attrs.exactWeight3;
        this.withdrawn = attrs.withdrawn;
        this.paid = attrs.paid;
        this.onlineRegistrationStatus = attrs.onlineRegistrationStatus;
        this.usaWrestlingCardNum = attrs.usaWrestlingCardNum && parseInt(attrs.usaWrestlingCardNum, null);
        this.identityPersonId = attrs.identityPersonId;
        this.source = attrs.source;
        this.record = attrs.record;
        this.seed = attrs.seed;

        if (attrs.location) {
            this.location = (new Location()).deserialize(attrs.location);
        }
        if (attrs.exactWeight) {
            this.exactWeight = parseFloat(attrs.exactWeight);
        }
        if (attrs.dateOfBirth) {
            this.dateOfBirth = convertToDateTime(attrs.dateOfBirth);
        }
        if (attrs.grade) {
            this.grade = (new Grade()).deserialize(attrs.grade);
        }

        // Fill WeightClass from relationships if it exists
        const weightClass: any = relationships.weightClass;
        if (weightClass &&
            included &&
            weightClass.data &&
            weightClass.data.id) {
            const wightClassId: string = weightClass.data.id;
            const weightClassInclude: Object = included.filter(
                (i: any) => i.type === 'weightClass' && i.id === wightClassId,
            )[0];

            if (weightClassInclude) {
                this.weightClass = (new WeightClass()).deserialize(weightClassInclude);
            }
        } else if (attrs.weightClassId) {
            this.weightClass = new WeightClass(attrs.weightClassId);
        }

        // Fill Skill Level from relationships if it exists
        const skillLevelRelationship: any = relationships.skillLevel;
        if (skillLevelRelationship &&
            included &&
            skillLevelRelationship.data &&
            skillLevelRelationship.data.id) {
            const skillLevelId: string = skillLevelRelationship.data.id;
            const skillLevelInclude: any = included.filter(
                (i: any) => i.type === 'skillLevel' && i.id === skillLevelId,
            )[0];

            if (skillLevelInclude) {
                this.skillLevel = (new SkillLevel()).deserialize(skillLevelInclude);
            }
        } else if (attrs.skillLevelId) {
            this.skillLevel = new SkillLevel();
            this.skillLevel.id = attrs.skillLevelId;
        }

        // Fill Team from relationships if it exists
        const teamRelationship: any = relationships.team;
        if (teamRelationship &&
            included &&
            teamRelationship.data &&
            teamRelationship.data.id) {
            const teamId: string = teamRelationship.data.id;
            const teamInclude: any = included.filter((i: any) => i.type === 'team' && i.id === teamId)[0];

            if (teamInclude) {
                this.team = (new Team(this.eventId)).deserialize(teamInclude);
            }
        } else if (attrs.teamId) {
            this.team = new Team(this.eventId, attrs.teamId, false);
        }

        // Fill Division from relationships if it exists
        const divisionRelationship: any = relationships.division;
        if (divisionRelationship &&
            included &&
            divisionRelationship.data &&
            divisionRelationship.data.id) {
            const divisionId: string = divisionRelationship.data.id;
            const divisionInclude: any = included.filter((i: any) => i.type === 'division' && i.id === divisionId)[0];

            if (divisionInclude) {
                this.division = (new Division(this.eventId)).deserialize(divisionInclude, null);
            }
        } else if (attrs.divisionId) {
            this.division = new Division(this.eventId);
            this.division.id = attrs.divisionId;
        }

        const waiverLinks: any = relationships.waivers;
        if (waiverLinks &&
            included &&
            waiverLinks.data) {
            this.waivers = [];

            for (const waiverLink of waiverLinks.data) {
                const waiver: any = included.filter((i: any) => i.id === waiverLink.id)[0];
                if (waiver) {
                    this.waivers.push((new WrestlerWaiver()).deserialize(waiver));
                }
            }
        }

        const seedingCriteriaLinks: any = relationships.seedingCriteria;
        if (seedingCriteriaLinks &&
            included &&
            seedingCriteriaLinks.data) {
            this.seedingCriteria = [];

            for (const seedingCriteriaLink of seedingCriteriaLinks.data) {
                const seedingCriteria: any = included.filter((i: any) => i.id === seedingCriteriaLink.id)[0];
                if (seedingCriteria) {
                    this.seedingCriteria.push((new WrestlerSeedingCriteria()).deserialize(seedingCriteria, included));
                }
            }
        }

        // Fill createdByUserAccount from relationships if it exists
        const userAccountRelationship: any = relationships.createdByUserAccount;
        if (userAccountRelationship &&
            included &&
            userAccountRelationship.data &&
            userAccountRelationship.data.id) {
            const userAccountId: string = userAccountRelationship.data.id;
            const userAccountInclude: any = included.filter(
                (i: any) => i.type === 'userAccount' && i.id === userAccountId,
            )[0];

            if (userAccountInclude) {
                this.createdByUserAccount = (new UserAccount()).deserialize(userAccountInclude);
            }
        } else if (attrs.createdByUserId) {
            this.createdByUserAccount = new UserAccount();
            this.createdByUserAccount.id = attrs.createdByUserId;
        }

        return this;
    }
}
